import { BaseCard } from '@/components/uiKit/Cards';
import React, { useState, useMemo } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { RootState } from '@/store/store';
import { useSelector } from 'react-redux';
import { useActiveAssetsType, useAppFilters, useQueryNavigation } from '@/hooks';
import { ASSET_STUDIO, FILTER_PARAMS } from '@/types/shared/navigation-tabs.enums';
import { sortComponentsByHealthStatusAndName } from '../../../SidePane/ComponentSelector/util';
import { Success } from '@/assets/Icons';
import { Icon } from '@/components/shared/display/Icon';
import { AccordionSection } from '@/components/uiKit/FiltersGroup/AccordionSection';
import {
  DiagnosticsChevron,
  ItemDetailsContent,
  ItemDetailsHeader,
} from '@/components/pages/AssetsPreviewPage/AssetsOverview/AssetPreviewTabsViews/Faults/components/ItemsDrawerContent';
import { Asset } from '@/types/models/assets.types';
import { DiagnosticsHeader } from '../_common/DiagnosticsHeader';
import { DEFAULT_FAULT_TYPE, DeepPartial, Fault } from '@/types/models/faults.types';
import { formatAssetName } from '@/utils/shared';
import { SENSORS_DATA_TYPE } from '@/const';

interface ObservationsAndActionsProps {
  isLoading: boolean;
  asset?: Asset;
}

enum CardType {
  ALL = 'all',
}

/** Builds evidence_data from evidence_data (preferred), sources_info, or evidences_text (old structure).
 *  Always includes camera sources from sources_info even if evidence_data exists. */
function buildEvidenceData(data: any): { text: string; resource_id: string | null; source_type?: string | null }[] {
  // Get camera source from sources_info (may not be present in evidence_data)
  const cameraSource = data.sources_info?.find(
    (s: any) => s.source_type?.toLowerCase() === 'camera'
  );
  const cameraEntry = cameraSource
    ? { text: cameraSource.evidence_text, resource_id: cameraSource.evidence_resource_id || null, source_type: 'camera' }
    : null;

  if (data.evidence_data?.length > 0) {
    // If evidence_data already has a camera entry, use as-is; otherwise append camera source
    const hasCamera = data.evidence_data.some((e: any) => e.source_type?.toLowerCase() === 'camera');
    if (hasCamera || !cameraEntry) return data.evidence_data;
    return [...data.evidence_data, cameraEntry];
  }
  const fromSources = data.sources_info
    ?.filter((s: any) => s.evidence_text || s.evidence_resource_id || s.source_type?.toLowerCase() === 'camera')
    .map((s: any) => ({ text: s.evidence_text, resource_id: s.evidence_resource_id || null, source_type: s.source_type || null }));
  if (fromSources?.length > 0) return fromSources;
  return data.evidences_text?.map((text: string) => ({ text, resource_id: null })) || [];
}

/** Transforms a fault from the faults API into a component-shaped entry for display */
function transformFaultToComponent(fault: any, components: any[]) {
  const component = components.find((c: any) => c.component_id === fault.component_id) || {};
  return {
    ...component,
    component_id: `${fault.component_id}-${fault.fault_type}`,
    original_component_id: fault.component_id,
    component_name: fault.component_name,
    component_ui_name: fault.component_name,
    unit_ui_name: fault.unit_ui_name,
    health_status: fault.health_status,
    evidences_text: fault.evidence_data?.map((e: any) => e.text) || fault.evidence_text || [],
    evidence_data: buildEvidenceData(fault),
    actions_descriptions: fault.prescriptive_actions || [],
    possible_causes: fault.possible_causes || [],
    maintenance_reference: fault.maintenance_reference,
    diagnostic_ids: fault.diagnostic_ids || [],
    sources_info: fault.sources_info || [],
    diagnostics: [
      {
        fault_type: fault.fault_type,
        diagnostic_created_at: fault.diagnostic_created_at,
        maintenance_reference: fault.maintenance_reference,
      },
    ],
  };
}

function renderObservationsAndActionsCard({
  type,
  item,
  expandedComponents,
  setExpandedComponents,
  truncatedComponents,
  setTruncatedComponents,
  onViewSensorData,
  selectedComponent,
  selectedDiagnostic,
  asset,
  isMobileAsset,
}: {
  type: CardType;
  item?: any;
  expandedComponents?: Set<string>;
  setExpandedComponents?: (value: Set<string>) => void;
  truncatedComponents?: Set<string>;
  setTruncatedComponents?: React.Dispatch<React.SetStateAction<Set<string>>>;
  onViewSensorData?: (componentId: string) => void;
  asset?: Asset;
  isMobileAsset?: boolean;
  selectedComponent?: string;
  selectedDiagnostic?: any;
}) {
  const componentsData = item?.components || [];

  if (type === CardType.ALL) {
    // Filter components that have both observations and actions
    // Support both old structure (evidences_text/actions_descriptions) and transformed structure
    const filteredComponents = componentsData.filter((component: any) => {
      // Check for observations in multiple possible locations
      const hasObservations =
        (component.evidences_text && component.evidences_text.length > 0) ||
        (component.evidence_data && component.evidence_data.length > 0) ||
        (component.evidence_text && component.evidence_text.length > 0);

      // Check for actions in multiple possible locations
      const hasActions =
        (component.actions_descriptions && component.actions_descriptions.length > 0) ||
        (component.prescriptive_actions && component.prescriptive_actions.length > 0);

      // console.log('🔍 Filtering component:', component.component_name || component.component_ui_name, {
      //   hasObservations,
      //   hasActions,
      //   evidences_text: component.evidences_text,
      //   actions_descriptions: component.actions_descriptions
      // });

      return hasObservations && hasActions;
    });

    // Sort filtered components by health status and name
    const sortedComponents = sortComponentsByHealthStatusAndName(filteredComponents);

    // Filter out healthy and N/A components without real faults
    const faultComponents = sortedComponents.filter((component: any) => {
      const { health_status } = component || {};
      const fault_type = component.diagnostics?.[0]?.fault_type;
      const isHealthyOrNA = health_status === 'healthy' || health_status === 'na';
      const hasNoFaultType = !fault_type || fault_type === 'fault_type';
      // Only show if it has a real fault (not healthy/NA or has a valid fault_type)
      return !(isHealthyOrNA && hasNoFaultType);
    });

    if (faultComponents.length === 0) {
      // Get component name - try multiple sources
      let componentName = 'Component';

      // Try to get from first component in the data
      const firstComponent = sortedComponents[0] || componentsData[0];
      if (firstComponent?.component_ui_name || firstComponent?.component_name) {
        componentName = firstComponent?.component_ui_name || firstComponent?.component_name;
      }

      // If we're viewing a specific component, try to get the name from selectedDiagnostic
      if (selectedComponent && selectedComponent !== ASSET_STUDIO.ALL_COMPONENTS) {
        const comp = selectedDiagnostic?.[1]?.assets?.[0]?.components?.find(
          (c: any) => c.component_id === selectedComponent
        );
        if (comp) {
          componentName = comp.component_ui_name || comp.component_name || componentName;
        }
      }

      const isHealthy = firstComponent?.health_status === 'healthy';

      return (
        <BaseCard
          variant="base"
          classNames={{
            card: 'w-full h-full rounded-lg',
            content: 'flex items-center flex-col items-center justify-center text-center p-8 gap-2 h-full',
          }}
        >
          <>
            <Icon icon={Success} className="w-14 h-14" />
            <div className="text-lg font-semibold text-[#01152B]">No Faults Detected</div>
            <div className="text-[#55687D] font-medium">
              {isHealthy
                ? `${componentName} is healthy and operating normally`
                : `No diagnostics data available for ${componentName}`}
            </div>
          </>
        </BaseCard>
      );
    }
    // Use passed asset prop (has full data with supplier/model) for display name
    const assetUiName = formatAssetName(asset, isMobileAsset ?? false);

    return (
      <div className="flex flex-col flex-1 gap-2 h-fit">
        {faultComponents.map((data: any) => {
          // Check if this component has camera data
          const diagnosticHasCamera = data.sources_info?.some(
            (src: any) => src.source_type?.toLowerCase() === SENSORS_DATA_TYPE.Camera
          );

          const faultItem: DeepPartial<Fault> = {
            fault_type: {
              name:
                data.diagnostics?.[0]?.fault_type && data.diagnostics?.[0]?.fault_type !== 'fault_type'
                  ? data.diagnostics?.[0]?.fault_type
                  : DEFAULT_FAULT_TYPE,
            },
            health_status: data.health_status,
            location: {
              asset_id: asset?.asset_id || selectedDiagnostic?.[1]?.assets?.[0]?.asset_id,
              asset_ui_name: assetUiName,
              // Use original_component_id for navigation (component_id might be a unique fault ID)
              component_id: data?.original_component_id || data?.component_id,
              component_ui_name: data?.component_ui_name || data?.component_name,
            },

            work_orders: data?.maintenance_reference
              ? [{ wo_id: data.maintenance_reference, wo_reference: data.maintenance_reference }]
              : [],

            evidence: {
              evidence_data: buildEvidenceData(data),
              possible_causes: data.possible_causes,
              prescriptive_actions: data.actions_descriptions,
            },
          };

          return (
            <AccordionSection
              key={`${data.component_id}-${selectedComponent}`}
              variant="card"
              defaultOpen={faultComponents?.length === 1}
              chevronPosition="left"
              label={
                <div className=" flex flex-1 w-full px-6 pl-0 items-start">
                  <ItemDetailsHeader item={faultItem} showAssetLink={false} showComponentLink={false} hasCamera={diagnosticHasCamera} />
                </div>
              }
              customChevron={<DiagnosticsChevron />}
              showViewDetailsText
              content={<ItemDetailsContent item={faultItem as Partial<Fault>} hasCamera={diagnosticHasCamera} />}
            />
          );
        })}
      </div>
    );
  }
  // This code path is no longer used since we always use CardType.ALL
  return null;
}

export function ObservationsAndActions({ isLoading, asset }: ObservationsAndActionsProps) {
  const [expandedComponents, setExpandedComponents] = useState<Set<string>>(new Set());
  const [truncatedComponents, setTruncatedComponents] = useState<Set<string>>(new Set());
  const { selectedComponent } = useAppFilters();
  const { navigateWithQuery } = useQueryNavigation();
  const { isMobileAsset } = useActiveAssetsType();

  const handleViewSensorData = (componentId: string) => {
    navigateWithQuery(window.location.pathname, {
      replaceParams: {
        [FILTER_PARAMS.ASSET_STUDIO]: ASSET_STUDIO.SENSOR_DATA,
        [FILTER_PARAMS.SELECTED_COMPONENT]: componentId,
      },
    });
  };

  const { selectedDiagnostic, useFaultsField } = useSelector((state: RootState) => state.assetsStudio);
  const selectedAssetDiagnostics = selectedDiagnostic?.[1]?.assets?.[0];

  // Transform faults data to component structure when using new implementation
  const transformedComponentsData = useMemo(() => {
    // console.log('🔄 Transformation Mode:', useFaultsField ? 'NEW FAULTS' : 'OLD DIAGNOSTICS');
    // console.log('📦 Raw Asset Data:', selectedAssetDiagnostics);

    if (!useFaultsField) {
      // Use old implementation - for mobile assets, the backend already provides evidences_text and actions_descriptions
      // console.log('📦 OLD - Using diagnostics, components already have evidences_text/actions_descriptions');
      return selectedAssetDiagnostics;
    }

    // New implementation - Transform faults field to component structure for display
    if (!selectedAssetDiagnostics?.faults) {
      // console.log('⚠️ No faults field found in asset data');
      return selectedAssetDiagnostics;
    }

    const faults = selectedAssetDiagnostics.faults || [];
    const components = selectedAssetDiagnostics.components || [];

    // console.log('✨ NEW - Faults array:', faults);

    // Map each fault to a separate component entry (show faults one by one)
    const componentsWithFaults = faults.map((fault: any) =>
      transformFaultToComponent(fault, components)
    );

    // Sort by health status first, then by name using existing utility
    const sortedComponents = sortComponentsByHealthStatusAndName(componentsWithFaults);

    return {
      ...selectedAssetDiagnostics,
      components: sortedComponents,
    };
  }, [useFaultsField, selectedAssetDiagnostics]);

  const selectedData = useMemo(() => {
    if (selectedComponent === ASSET_STUDIO.ALL_COMPONENTS) {
      return null;
    }

    if (!useFaultsField) {
      // Old implementation
      return selectedDiagnostic?.[1]?.assets?.[0]?.components?.find((el: any) => el.component_id === selectedComponent);
    }

    // New implementation - get faults data from asset level and filter for selected component
    const assetData = selectedDiagnostic?.[1]?.assets?.[0];
    if (!assetData?.faults) {
      return null;
    }

    const faults = assetData.faults || [];
    const components = assetData.components || [];

    // Filter faults for the selected component only
    const componentFaults = faults.filter((fault: any) => fault.component_id === selectedComponent);

    // Transform each fault into a separate component entry
    const selectedComponentFaults = componentFaults.map((fault: any) =>
      transformFaultToComponent(fault, components)
    );

    // Sort by health status and name
    return sortComponentsByHealthStatusAndName(selectedComponentFaults);
  }, [useFaultsField, selectedDiagnostic, selectedComponent]);

  const { selectedComponents } = useSelector((state: RootState) => state.assetsStudio);

  if (isLoading) return <Skeleton className="flex flex-1 min-h-[400px]" />;

  return (
    <BaseCard
      variant="wrapper"
      classNames={{
        card: 'min-h-0 flex flex-col h-full  ',
        content: 'h-full flex w-full flex-1 bg-gray-200 gap-2 overflow-auto scrollbar-thin p-0',
      }}
      title={
        <DiagnosticsHeader
          selectedComponent={selectedComponent}
          selectedDiagnostic={selectedDiagnostic}
          selectedData={selectedData}
          selectedComponents={selectedComponents}
          asset={asset}
        />
      }
    >
      {renderObservationsAndActionsCard({
        type: CardType.ALL,
        item:
          selectedComponent === ASSET_STUDIO.ALL_COMPONENTS
            ? transformedComponentsData // Use transformed data
            : { components: Array.isArray(selectedData) ? selectedData : selectedData ? [selectedData] : [] },
        expandedComponents,
        setExpandedComponents,
        truncatedComponents,
        setTruncatedComponents,
        onViewSensorData: handleViewSensorData,
        selectedComponent,
        selectedDiagnostic,
        asset,
        isMobileAsset,
      })}
    </BaseCard>
  );
}
